import type { JsonRecord } from "@postdaily/api/lib/json";
import { env } from "@postdaily/env/web";
import type { BlogPost } from "@/components/marketing/blog-data";
import { blogSocialImagePath } from "@/components/marketing/blog-images";
import type { Comparison } from "@/components/marketing/comparison-data";
import type { HelpArticle } from "@/components/marketing/help-data";
import type { LimitsPage } from "@/components/marketing/limits-data";
import type { NetworkLanding } from "@/components/marketing/network-landing-data";
import { COMPANY } from "./company";
import { joinWithAnd } from "./join-with-and";
import {
  type CatalogPlan,
  type CatalogPrice,
  hasPaidPrices,
} from "./plan-prices";
import { inReviewPlatformLabels, platformLabels } from "./platforms";
import { PRICING_FAQS } from "./pricing-faqs";
import { SOCIAL_PROFILE_URLS } from "./social-profiles";

/**
 * The live and upcoming networks, derived from the one source of truth rather
 * than written out — `PLATFORM_CATALOG` also drives the integrations grid and
 * the connect surface, so flipping a provider to `status: "live"` updates these
 * descriptions with it. The previous hardcoded copy ("every major network",
 * "Bluesky, Mastodon and Facebook are live today") is exactly what goes stale
 * otherwise. `MOCK_PLATFORM` is a separate export, so no test channel is
 * counted here.
 */
const LIVE_NETWORKS = platformLabels("live");
const SOON_NETWORKS = platformLabels("soon");
const LIVE_NETWORK_COUNT = LIVE_NETWORKS.length;

/** Shared by `SITE` and the home page so the two can never drift apart. */
/**
 * Live networks with the big ones first. Those are what people search for
 * ("instagram scheduler", "schedule tiktok posts"), and a title or snippet cut
 * off after three names should still show the ones that matter to them.
 */
const NETWORK_SEARCH_ORDER = [
  "Instagram",
  "TikTok",
  "YouTube",
  "Facebook",
  "Threads",
  "Pinterest",
  "X",
  "LinkedIn",
  "Bluesky",
  "Mastodon",
];

const LIVE_NETWORKS_BIG_FIRST = [...LIVE_NETWORKS].sort(
  (a, b) =>
    (NETWORK_SEARCH_ORDER.indexOf(a) + 1 || NETWORK_SEARCH_ORDER.length + 1) -
    (NETWORK_SEARCH_ORDER.indexOf(b) + 1 || NETWORK_SEARCH_ORDER.length + 1),
);

// "from one calendar" went when LinkedIn made ten names: with it the snippet
// ran to 167 characters and search results cut the end.
const HOME_DESCRIPTION = `Schedule posts to ${joinWithAnd(LIVE_NETWORKS_BIG_FIRST)}. Free plan. Posts that actually go out.`;

const [TOP_A, TOP_B, TOP_C] = LIVE_NETWORKS_BIG_FIRST;
const INTEGRATIONS_TITLE =
  LIVE_NETWORK_COUNT > 3
    ? `Schedule to ${TOP_A}, ${TOP_B}, ${TOP_C} & ${LIVE_NETWORK_COUNT - 3} More — PostDaily`
    : `Integrations: ${joinWithAnd(LIVE_NETWORKS_BIG_FIRST)} — PostDaily`;

const DEFAULT_DESCRIPTION = `Plan your content, fill your posting queue and publish to ${LIVE_NETWORK_COUNT} networks — with an AI composer, an automatic queue and publishing that never drops a post.`;

/**
 * Names every live network: this is the snippet a search engine (and an
 * assistant reading it) quotes for "what does PostDaily post to".
 */
const IN_REVIEW_NETWORKS = inReviewPlatformLabels();
const IN_REVIEW_NOTE =
  IN_REVIEW_NETWORKS.length > 0
    ? ` ${joinWithAnd(IN_REVIEW_NETWORKS)} ${IN_REVIEW_NETWORKS.length === 1 ? "is" : "are"} in review.`
    : "";

const INTEGRATIONS_DESCRIPTION =
  SOON_NETWORKS.length > 0
    ? `${LIVE_NETWORK_COUNT} networks are live today — ${joinWithAnd(LIVE_NETWORKS)} — with ${joinWithAnd(SOON_NETWORKS)} next.${IN_REVIEW_NOTE}`
    : // The in-review note takes the closing line's place: all ten names
      // plus both would run past what a search result shows.
      `${LIVE_NETWORK_COUNT} networks are live today — ${joinWithAnd(LIVE_NETWORKS)}.${IN_REVIEW_NOTE || " One composer and one calendar for all of them."}`;

export const SITE = {
  name: "PostDaily",
  url: env.VITE_SITE_URL.replace(/\/$/, ""),
  defaultTitle: "PostDaily — Post every day, without the daily grind",
  defaultDescription: DEFAULT_DESCRIPTION,
  ogImage: "/og.jpg",
  logo: "/logo.png",
  supportEmail: "support@postdaily.app",
} as const;

/**
 * Hosts whose pages belong in a search index.
 *
 * Every other build — staging, local, a preview — emits `noindex` sitewide.
 * Without this, staging.postdaily.app answers 200 with a self-referencing
 * canonical for the *same* content as production and competes with it: the
 * staging robots.txt is a copy of production's, so it says `Allow: /`.
 */
const INDEXABLE_HOSTS = new Set(["www.postdaily.app", "postdaily.app"]);

/** False on staging and local builds, so `createPageHead` defaults to noindex. */
const IS_INDEXABLE_SITE = INDEXABLE_HOSTS.has(new URL(SITE.url).hostname);

export type PageMeta = {
  path: string;
  title: string;
  description: string;
  /** When true, emit robots noindex/nofollow (e.g. checkout success). */
  noIndex?: boolean;
  ogType?: "website" | "article";
};

export const PAGE_META = {
  home: {
    path: "/",
    title: `PostDaily — Social Media Scheduler for ${LIVE_NETWORK_COUNT} Networks`,
    description: HOME_DESCRIPTION,
  },
  about: {
    path: "/about",
    title: "About PostDaily — Who Builds It and Why",
    description:
      "PostDaily is a social media scheduler run by Galaxyway AI Ltd, a UK company. Why it exists, what it posts to, and how publishing stays reliable.",
  },
  blueskyBestTime: {
    path: "/tools/bluesky-best-time",
    title: "Bluesky best time to post — free analyser",
    description:
      "Check when your own Bluesky posts actually do best. Free, no login, no app password — and honest about when there is not enough data to tell.",
  },
  tools: {
    path: "/tools",
    title: "Free social media tools — no signup, nothing uploaded",
    description:
      "Free tools for social media managers: a 2027 content calendar, a bulk CSV checker, a thread splitter, a video checker and more. No account needed.",
  },
  blueskyEngagementRate: {
    path: "/tools/bluesky-engagement-rate",
    title: "Bluesky engagement rate calculator — free, no login",
    description:
      "Check any Bluesky account's real engagement rate from its own posts. Median not mean, so one viral post can't flatter the number. No login, no app password.",
  },
  characterCounter: {
    path: "/tools/character-counter",
    title: "Character Counter for X, Instagram & Bluesky — PostDaily",
    description:
      "Paste a post and see if it fits on X, Instagram, TikTok, Bluesky, Threads, YouTube and more, counted each network's own way: links, emoji and bytes included.",
  },
  threadSplitter: {
    path: "/tools/thread-splitter",
    title: "Twitter thread maker & splitter for X, Bluesky and Mastodon",
    description:
      "Paste a long draft and get a numbered thread (a reply chain) that fits X, Bluesky, Mastodon or Threads, counted the way each network counts links and emoji.",
  },
  videoChecker: {
    path: "/tools/video-checker",
    title: "Will this video upload? Check codec, size and length per network",
    description:
      "Drop a video and see which social networks will accept it. Codec, resolution and duration read in your own browser — the file is never uploaded. Free, no login.",
  },
  mastodonBestTime: {
    path: "/tools/mastodon-best-time",
    title: "Mastodon posting-time analyser for your own account",
    description:
      "Find when your own Mastodon posts do best: it reads your public posts by hour in your timezone, and says plainly when there isn't enough data.",
  },
  mastodonLimits: {
    path: "/tools/mastodon-limits",
    title: "Mastodon character limit checker — your server's real limits",
    description:
      "Your Mastodon server's character limit probably isn't 500. Check the real limit, link cost, media and alt-text caps for any server — free, no login.",
  },
  bulkCsvChecker: {
    path: "/tools/bulk-csv-checker",
    title: "Social media CSV template & bulk upload checker — free",
    description:
      "Check a bulk-scheduling CSV before you upload it: dates, image links, character limits and image rules for Instagram, TikTok, Pinterest and more, row by row.",
  },
  contentCalendar2027: {
    path: "/tools/content-calendar-2027",
    title: "2027 social media content calendar template (free CSV)",
    description:
      "Every 2027 date worth posting for, with a post idea each, as a free CSV for Google Sheets or Excel that imports straight into a scheduler.",
  },
  limits: {
    path: "/limits",
    title: "Social Media Limits by Network: Video, Image & Text — PostDaily",
    description:
      "Video length, file size, image dimensions and character limits for Instagram, YouTube Shorts, TikTok and Pinterest, each with its source and check date.",
  },
  clientApprovals: {
    path: "/client-approvals",
    title: "Social Media Client Approval Without Logins — PostDaily",
    description:
      "Send clients a review link: they approve posts or ask for changes with no account, and nothing publishes until they do. How it compares with 9 other tools.",
  },
  aiAgents: {
    path: "/ai-agents",
    title: "Schedule Posts from ChatGPT, Claude or Any AI Agent (MCP)",
    description:
      "Connect PostDaily to Claude, ChatGPT, Cursor or any MCP app to draft, schedule and publish social posts from a chat. Free on every plan; it asks before posting.",
  },
  alternatives: {
    path: "/alternatives",
    title: "Social Media Scheduler Alternatives, Compared — PostDaily",
    description:
      "PostDaily compared with Buffer, Hootsuite, Later, Sprout Social, Publer and more: where each one wins, who should stay put, and prices checked on a date.",
  },
  blog: {
    path: "/blog",
    title: "Social Media Scheduling Guides — PostDaily",
    description:
      "Practical guides to scheduling social posts: how each network actually works, where its limits are, and the point at which a tool is worth it.",
  },
  careers: {
    path: "/careers",
    title: "Careers — PostDaily",
    description:
      "Help build the calm internet. We're a small, remote-first team that cares about craft, reliability and the people who use what we make.",
  },
  changelog: {
    path: "/changelog",
    title: "Changelog — PostDaily",
    description:
      "What's new in PostDaily: every improvement, fix and new feature we ship to the social media scheduler, with the date each one went live.",
  },
  contact: {
    path: "/contact",
    title: "Contact — PostDaily",
    description:
      "Questions, feedback, or a network you want us to add? Contact PostDaily support and a real person will get back to you within one business day.",
  },
  cookiePolicy: {
    path: "/cookie-policy",
    title: "Cookie Policy — PostDaily",
    description:
      "The cookies and similar technologies PostDaily uses, why each one is there, which are optional, and how to change your choice at any time.",
  },
  dataDeletion: {
    path: "/data-deletion",
    title: "Data Deletion — PostDaily",
    description:
      "How to delete your PostDaily account and personal data — including data from connected accounts like Facebook — or request deletion by email.",
  },
  features: {
    path: "/features",
    title: "Features: Calendar, Queue, AI & Approvals — PostDaily",
    description: `A drag-and-drop calendar, per-channel posting queues, an AI composer, first comments, bulk CSV upload and client approvals, across ${LIVE_NETWORK_COUNT} networks.`,
  },
  gdpr: {
    path: "/gdpr",
    title: "GDPR & Data Protection — PostDaily",
    description:
      "How PostDaily meets its obligations under the UK GDPR — your rights, our lawful bases, international transfers and subprocessors.",
  },
  help: {
    path: "/help",
    title: "PostDaily Help Center — Connect, Schedule, Publish",
    description:
      "Guides, answers and support for PostDaily — getting started, connecting channels, scheduling, billing and troubleshooting.",
  },
  integrations: {
    path: "/integrations",
    title: INTEGRATIONS_TITLE,
    description: INTEGRATIONS_DESCRIPTION,
  },
  partners: {
    path: "/partners",
    title: "Creator Partner Program — PostDaily",
    description:
      "Earn recurring commission on every customer you refer to PostDaily, give your audience a real discount, and track it all from a private partner dashboard.",
  },
  partnersTerms: {
    path: "/partners/terms",
    title:
      "PostDaily Creator Partner Program Terms | Affiliate & Referral Terms",
    description:
      "The terms of the PostDaily Creator Partner Program: referral tracking, recurring commissions, payouts, promotion rules and partner responsibilities.",
  },
  pricing: {
    path: "/pricing",
    // The prerendered page overrides both with the live lowest price; see
    // `pricingHeadMeta` in `plan-prices.ts`. This is the fallback when the
    // catalog had no paid prices at build time.
    title: "PostDaily Pricing — Free Plan and Paid Plans",
    description:
      "A free plan, and paid plans with a channel allowance instead of per-channel billing. Compare every plan's limits and what each one includes.",
  },
  privacy: {
    path: "/privacy",
    title: "Privacy Policy — PostDaily",
    description:
      "How PostDaily collects, uses and safeguards your information and your connected social accounts' data, and the control you have over all of it.",
  },
  terms: {
    path: "/terms",
    title: "Terms of Service — PostDaily",
    description:
      "PostDaily's terms of service: the agreement between you and Galaxyway AI Ltd for using PostDaily, written to be as plain and fair as we can make it.",
  },
  success: {
    path: "/success",
    title: "You're all set — PostDaily",
    description:
      "Your PostDaily subscription is active. Head to your queue to connect channels and start scheduling posts on your new plan's allowance.",
    noIndex: true,
  },
} as const satisfies Record<string, PageMeta>;

export type PageMetaKey = keyof typeof PAGE_META;

/** A JSON-LD block. Serialized straight into a <script>, so it IS JSON. */
type JsonLd = JsonRecord;

/** What a route's `head()` returns: meta tags, links, and optional JSON-LD. */
type SeoHead = {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
  scripts?: HeadScript[];
};

type HeadScript = {
  type: string;
  children: string;
};

type CreatePageHeadExtras = {
  /** Override title (e.g. dynamic blog posts). */
  title?: string;
  /** Override description. */
  description?: string;
  /** Override path for canonical/OG URL. */
  path?: string;
  image?: string;
  imageAlt?: string;
  imageHeight?: number;
  imageWidth?: number;
  ogType?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
};

function jsonLdScript(data: JsonLd | JsonLd[]): HeadScript {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  };
}

/** Build an absolute URL from a site-relative path. */
export function absoluteUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE.url}${normalized}`;
}

/** Stable node id, so other JSON-LD blocks reference the org instead of restating it. */
const ORGANIZATION_ID = `${SITE.url}/#organization`;

export function organizationJsonLd(): JsonLd {
  const organization: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE.name,
    url: SITE.url,
    logo: absoluteUrl(SITE.logo),
    legalName: COMPANY.legalName,
    email: SITE.supportEmail,
    description: SITE.defaultDescription,
    foundingDate: "2026-07",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: SITE.supportEmail,
      url: absoluteUrl("/contact"),
      availableLanguage: "English",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.address.streetAddress,
      addressLocality: COMPANY.address.addressLocality,
      addressRegion: COMPANY.address.addressRegion,
      postalCode: COMPANY.address.postalCode,
      addressCountry: COMPANY.address.addressCountry,
    },
  };

  /*
    `sameAs` is how search and AI engines merge a brand's scattered mentions
    into one entity. The list lives in `social-profiles` because the footer and
    contact page link the same accounts; the guard stays so a build with the
    list emptied omits the key rather than emitting an empty array.
  */
  if (SOCIAL_PROFILE_URLS.length > 0) {
    organization.sameAs = [...SOCIAL_PROFILE_URLS];
  }

  return organization;
}

const BILLING_INTERVALS = [
  { duration: "P1M", key: "monthly", label: "billed monthly" },
  { duration: "P1Y", key: "annual", label: "billed annually" },
] as const;

/** schema.org wants a plain decimal string; amounts arrive in minor units. */
function offerPrice(price: CatalogPrice): string {
  return (price.amount / 100).toFixed(2);
}

/**
 * One `Offer` per plan and billing interval, straight from the catalog the
 * pricing route's loader read. Free is a single offer: it has no interval to
 * choose between, and two $0 offers would only describe the same thing twice.
 */
function planOffers(plans: readonly CatalogPlan[]): JsonLd[] {
  const url = absoluteUrl(PAGE_META.pricing.path);

  return plans.flatMap((plan): JsonLd[] => {
    if (plan.planGroup === "free") {
      const price = plan.monthly ?? plan.annual;

      return price
        ? [
            {
              "@type": "Offer",
              name: plan.displayName,
              price: offerPrice(price),
              priceCurrency: price.currency.toUpperCase(),
              url,
            },
          ]
        : [];
    }

    return BILLING_INTERVALS.flatMap(({ duration, key, label }): JsonLd[] => {
      const price = plan[key];

      if (!price) {
        return [];
      }

      const amount = offerPrice(price);
      const currency = price.currency.toUpperCase();

      return [
        {
          "@type": "Offer",
          name: `${plan.displayName}, ${label}`,
          price: amount,
          priceCurrency: currency,
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: amount,
            priceCurrency: currency,
            billingDuration: duration,
          },
          url,
        },
      ];
    });
  });
}

/**
 * `SoftwareApplication` for the product pages — the vocabulary comparison
 * engines and AI shopping surfaces read to answer "what is this and what does
 * it cost".
 *
 * Pass the live catalog (the pricing route's loader data) to price every plan.
 * Without it — every other page, or a catalog read that came back with no paid
 * plans — only the free tier is priced. Prices are never written into the
 * source: they come from Polar through `billing.listPlans`, because a stale
 * price in structured data is worse than no price. No `aggregateRating` until
 * there are real reviews to aggregate.
 */
export function softwareApplicationJsonLd(
  plans?: readonly CatalogPlan[] | null,
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE.name,
    url: SITE.url,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Social media management",
    operatingSystem: "Web",
    description: SITE.defaultDescription,
    publisher: { "@id": ORGANIZATION_ID },
    // What the product does, in the terms the pages use; the networks come
    // from the catalog so this can't name one that isn't live.
    featureList: [
      `Schedule and publish to ${joinWithAnd(LIVE_NETWORKS_BIG_FIRST)}`,
      "Posting queue that fills each channel's weekly time slots",
      "Drag-and-drop content calendar",
      "AI composer",
      "First comments on Facebook, Instagram and YouTube",
      "Threads of posts on X, Threads, Bluesky and Mastodon",
      "Client approvals through a no-login review link",
      "MCP server: schedule and publish from any AI agent that supports MCP, including Claude, ChatGPT and Cursor",
      "Bulk scheduling from a CSV file",
      "Automatic retries and per-post failure messages",
      "Post and channel analytics",
    ],
    offers:
      plans && hasPaidPrices(plans)
        ? planOffers(plans)
        : {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            description:
              "Free plan, no credit card required. Paid plans add channels, team seats and history.",
            url: absoluteUrl("/pricing"),
          },
  };
}

export function websiteJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.url,
    description: SITE.defaultDescription,
    publisher: { "@id": ORGANIZATION_ID },
  };
}

export function faqJsonLd(
  faqs: ReadonlyArray<{ q: string; a: string }> = PRICING_FAQS,
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };
}

/**
 * `HowTo` structured data for a help article, so step-by-step guides can win
 * the rich result competitors like Publer already hold. Steps come from the
 * article's first `steps` block — articles without one fall back to a plain
 * `Article`, since a HowTo with no steps is invalid.
 */
export function helpArticleJsonLd(article: HelpArticle): JsonLd {
  const url = absoluteUrl(`/help/${article.slug}`);
  const steps = article.body.find((block) => block.type === "steps");

  // Attached under an `if` rather than spread from a `{…} | {}`: this block is
  // serialized straight into a <script type="application/ld+json">, and a
  // `video: undefined` key would vanish silently instead of being rejected.
  const video: JsonRecord | null = article.video
    ? {
        "@type": "VideoObject",
        name: article.title,
        description: article.video.caption,
        contentUrl: absoluteUrl(article.video.src),
        thumbnailUrl: absoluteUrl(article.video.poster ?? SITE.ogImage),
        uploadDate: article.updated,
      }
    : null;

  if (steps?.type === "steps") {
    const howTo: JsonLd = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: article.title,
      description: article.excerpt,
      // Freshness is one of the few signals AI answer engines demonstrably
      // reward, and this is the only place a help article declares one.
      dateModified: article.updated,
      step: steps.items.map((item, index) => ({
        "@type": "HowToStep",
        position: index + 1,
        name: item.title,
        text: item.text ?? item.title,
        url: `${url}#step-${index + 1}`,
      })),
    };

    if (video) {
      howTo.video = video;
    }

    return howTo;
  }

  const articleLd: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: absoluteUrl(SITE.ogImage),
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: absoluteUrl(SITE.logo) },
    },
    dateModified: article.updated,
    mainEntityOfPage: url,
  };

  if (video) {
    articleLd.video = video;
  }

  return articleLd;
}

/**
 * Home → Help center → the article.
 *
 * The visible trail also shows the article's category, but the category is
 * not a page — there is no per-category route — so it is left out here rather
 * than emitted as a name-only entry. Schema.org tolerates one; Google does
 * not: it requires an `item` URL on every level but the last, and flagged
 * every help article "Breadcrumbs: 1 invalid item" until the level was
 * dropped (Search Console, 2026-10-03).
 */
export function helpBreadcrumbJsonLd(article: HelpArticle): JsonLd {
  return breadcrumbJsonLd([
    { name: "Help center", path: "/help" },
    { name: article.title, path: `/help/${article.slug}` },
  ]);
}

export function articleJsonLd(post: BlogPost): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: absoluteUrl(blogSocialImagePath(post.slug)),
    // Posts are credited to PostDaily, the same entity as the site-wide
    // Organization, not to a person.
    author: {
      "@type": "Organization",
      "@id": ORGANIZATION_ID,
      name: SITE.name,
    },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: {
        "@type": "ImageObject",
        url: absoluteUrl(SITE.logo),
      },
    },
    datePublished: post.date,
    dateModified: post.updated ?? post.date,
    mainEntityOfPage: absoluteUrl(`/blog/${post.slug}`),
  };
}

/**
 * Build a full TanStack Router `head` payload for a marketing page:
 * title, description, Open Graph, Twitter cards, canonical, optional robots + JSON-LD.
 */
export function createPageHead(
  page: PageMeta,
  extras: CreatePageHeadExtras = {},
) {
  const title = extras.title ?? page.title;
  const description = extras.description ?? page.description;
  const path = extras.path ?? page.path;
  const url = absoluteUrl(path);
  const image = absoluteUrl(extras.image ?? SITE.ogImage);
  const ogType = extras.ogType ?? page.ogType ?? "website";
  const noIndex = extras.noIndex ?? page.noIndex ?? !IS_INDEXABLE_SITE;

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:type", content: ogType },
    { property: "og:site_name", content: SITE.name },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
  ];

  if (extras.imageAlt) {
    meta.push(
      { property: "og:image:alt", content: extras.imageAlt },
      { name: "twitter:image:alt", content: extras.imageAlt },
    );
  }

  if (extras.imageWidth && extras.imageHeight) {
    meta.push(
      { property: "og:image:width", content: String(extras.imageWidth) },
      { property: "og:image:height", content: String(extras.imageHeight) },
    );
  }

  if (noIndex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  }

  const scripts: HeadScript[] = [];
  if (extras.jsonLd) {
    scripts.push(jsonLdScript(extras.jsonLd));
  }

  const head: SeoHead = {
    meta,
    links: [{ rel: "canonical", href: url }],
  };

  // Omitted when empty: an empty `scripts` array still renders a <script> slot.
  if (scripts.length > 0) {
    head.scripts = scripts;
  }

  return head;
}

/**
 * Head for a per-network landing page. Title, description and FAQs all come
 * from `network-landing-data.ts`, so these pages are not listed in `PAGE_META`
 * — one network, one source of truth.
 *
 * Ships `FAQPage` (the FAQs are real, visible questions on the page) and
 * `SoftwareApplication`, since these are product pages a comparison engine
 * would want to read prices and category from.
 */
/**
 * A breadcrumb trail from the home page. Every level passed here is a real
 * page. A level without one (a category, say) is left out — never given an
 * invented URL, and never emitted name-only, which Google rejects.
 */
function breadcrumbJsonLd(
  trail: ReadonlyArray<{ name: string; path: string }>,
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{ name: "Home", path: "/" }, ...trail].map(
      (level, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: level.name,
        item: level.path === "/" ? SITE.url : absoluteUrl(level.path),
      }),
    ),
  };
}

/** Home → Guides → the post. */
export function blogBreadcrumbJsonLd(post: BlogPost): JsonLd {
  return breadcrumbJsonLd([
    { name: "Guides", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ]);
}

/** A `/limits/<network>` page: its FAQs and Home → Limits → page. */
export function limitsPageHead(page: LimitsPage) {
  return createPageHead(
    {
      path: `/limits/${page.slug}`,
      title: page.metaTitle,
      description: page.metaDescription,
    },
    {
      jsonLd: [
        faqJsonLd(page.faqs),
        breadcrumbJsonLd([
          { name: "Limits", path: "/limits" },
          { name: `${page.name} limits`, path: `/limits/${page.slug}` },
        ]),
      ],
    },
  );
}

export function networkLandingHead(landing: NetworkLanding) {
  return createPageHead(
    {
      path: `/${landing.slug}`,
      title: landing.metaTitle,
      description: landing.metaDescription,
    },
    {
      jsonLd: [
        faqJsonLd(landing.faqs),
        softwareApplicationJsonLd(),
        // Home → Integrations → this network's page.
        breadcrumbJsonLd([
          { name: "Integrations", path: "/integrations" },
          { name: `${landing.network} scheduler`, path: `/${landing.slug}` },
        ]),
      ],
    },
  );
}

/**
 * Head for an "alternative to X" page. Like the network landings, the content
 * is the single source of truth, so these are not listed in `PAGE_META`.
 */
export function comparisonHead(comparison: Comparison) {
  return createPageHead(
    {
      path: `/alternatives/${comparison.slug}`,
      title: comparison.metaTitle,
      description: comparison.metaDescription,
    },
    {
      jsonLd: [
        faqJsonLd(comparison.faqs),
        softwareApplicationJsonLd(),
        breadcrumbJsonLd([
          { name: "Compare", path: PAGE_META.alternatives.path },
          {
            name: `PostDaily vs ${comparison.competitor}`,
            path: `/alternatives/${comparison.slug}`,
          },
        ]),
      ],
    },
  );
}

/** `/client-approvals`: its FAQs and Home → Client approvals. */
export function clientApprovalsHead(
  faqs: ReadonlyArray<{ q: string; a: string }>,
) {
  const meta = PAGE_META.clientApprovals;
  return createPageHead(meta, {
    jsonLd: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([{ name: "Client approvals", path: meta.path }]),
    ],
  });
}

/** `/ai-agents`: its FAQs and Home → AI agents. */
export function aiAgentsHead(faqs: ReadonlyArray<{ q: string; a: string }>) {
  const meta = PAGE_META.aiAgents;
  return createPageHead(meta, {
    jsonLd: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([{ name: "AI agents", path: meta.path }]),
    ],
  });
}

/**
 * The `/alternatives` hub: an `ItemList` of every comparison, in the order the
 * page shows them, and Home → Compare.
 */
export function alternativesHubHead(
  comparisons: ReadonlyArray<Pick<Comparison, "competitor" | "slug">>,
) {
  const meta = PAGE_META.alternatives;
  return createPageHead(meta, {
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: meta.title,
        description: meta.description,
        numberOfItems: comparisons.length,
        itemListElement: comparisons.map((comparison, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: `PostDaily vs ${comparison.competitor}`,
          url: absoluteUrl(`/alternatives/${comparison.slug}`),
        })),
      },
      breadcrumbJsonLd([{ name: "Compare", path: meta.path }]),
    ],
  });
}

/**
 * The free public tools under /tools.
 *
 * These need their own schema rather than the product's. `softwareApplication\
 * JsonLd` names PostDaily at the site root, which is true of a product page and
 * false of a tool page: it told every engine that /tools/thread-splitter was
 * "PostDaily, a social media scheduler" living at the homepage URL, and shipped
 * six byte-identical entities across six distinct pages. The pages exist to be
 * found and cited for one narrow job each, so the markup has to say what the
 * page actually does.
 */
const TOOL_META_KEYS = [
  "bulkCsvChecker",
  "characterCounter",
  "contentCalendar2027",
  "blueskyBestTime",
  "blueskyEngagementRate",
  "mastodonBestTime",
  "mastodonLimits",
  "threadSplitter",
  "videoChecker",
] as const;

export type ToolPageKey = (typeof TOOL_META_KEYS)[number];

/**
 * `WebApplication` — the SoftwareApplication subtype for something that runs in
 * a browser, which is what each of these is. Every one computes on the visitor's
 * own input client-side, so `browserRequirements` is a fact rather than
 * boilerplate. Free with no account, and unlike the product there is no paid
 * tier to misprice, so the Offer is simply true.
 */
export function toolApplicationJsonLd(key: ToolPageKey): JsonLd {
  const meta = PAGE_META[key];
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: meta.title,
    url: absoluteUrl(meta.path),
    description: meta.description,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Social media management",
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript",
    isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
    publisher: { "@id": ORGANIZATION_ID },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Free to use with no account.",
      url: absoluteUrl(meta.path),
    },
  };
}

/**
 * Home → Free tools → this tool.
 *
 * "Free tools" carries a real `item` now that /tools exists. It was a name-only
 * entry while it did not — schema.org allows that, and it beat pointing a
 * breadcrumb at a URL that 404s.
 */
export function toolBreadcrumbJsonLd(key: ToolPageKey): JsonLd {
  const meta = PAGE_META[key];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
      {
        "@type": "ListItem",
        position: 2,
        name: "Free tools",
        item: absoluteUrl(PAGE_META.tools.path),
      },
      {
        "@type": "ListItem",
        position: 3,
        name: meta.title,
        item: absoluteUrl(meta.path),
      },
    ],
  };
}

/**
 * `ItemList` for the /tools index — the one page that names every tool.
 *
 * Takes keys rather than the catalog itself: the names and descriptions come
 * straight from `PAGE_META`, so what the list claims about a tool is by
 * construction what that tool's own page says, and no copy is written twice.
 * (Keys also avoid a circular import — `tools-catalog` types itself against
 * `ToolPageKey` from here.)
 */
export function toolListJsonLd(keys: ReadonlyArray<ToolPageKey>): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: PAGE_META.tools.title,
    description: PAGE_META.tools.description,
    numberOfItems: keys.length,
    itemListElement: keys.map((key, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: toolApplicationJsonLd(key),
    })),
  };
}

/**
 * Head for a /tools page: the tool's own `WebApplication`, its breadcrumb, and
 * `FAQPage` when the page renders real questions. Pass the same list the page
 * displays — structured data that answers questions the reader cannot see is
 * exactly what the FAQ guidelines forbid.
 */
export function toolPageHead(
  key: ToolPageKey,
  faqs?: ReadonlyArray<{ q: string; a: string }>,
) {
  const jsonLd: JsonLd[] = faqs ? [faqJsonLd(faqs)] : [];
  jsonLd.push(toolApplicationJsonLd(key), toolBreadcrumbJsonLd(key));
  return createPageHead(PAGE_META[key], { jsonLd });
}

/** Shortcut: createPageHead from a PAGE_META key. */
export function pageHead(key: PageMetaKey, extras: CreatePageHeadExtras = {}) {
  return createPageHead(PAGE_META[key], extras);
}

/** Head for authenticated / auth surfaces that must not be indexed. */
export function createNoIndexHead(title: string = SITE.name) {
  return {
    meta: [{ title }, { name: "robots", content: "noindex, nofollow" }],
  };
}

/** Root-route head pieces: icons + site-wide WebSite JSON-LD. */
export function rootSeoLinks() {
  return [
    { rel: "icon", type: "image/png", href: "/favicon.png" },
    { rel: "shortcut icon", type: "image/png", href: "/favicon.png" },
    { rel: "apple-touch-icon", href: "/icon.png" },
  ];
}

/**
 * Site-wide JSON-LD. Both nodes ship in one script: `websiteJsonLd` references
 * the organization by `@id`, so the org node has to travel with it.
 */
export function rootJsonLdScript(): HeadScript {
  return jsonLdScript([websiteJsonLd(), organizationJsonLd()]);
}
